/**
 * CommandPalette - Quick access to all features via Cmd+K
 *
 * BYK-163: Command Palette component (Cmd+K)
 *
 * Features:
 * - Fuzzy search across all actions
 * - Recent commands section
 * - Categorized results (Tools, AI, Layout, Settings)
 */

import { useCallback, useMemo, useState } from "react";
import { Command } from "cmdk";
import { useUIStore } from "@/stores/uiStore";
import { useFloorPlanStore } from "@/stores/floorPlan";

// ============================================================================
// Types
// ============================================================================

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  category: "layout" | "view" | "tools" | "ai" | "settings";
  icon?: string;
  shortcut?: string[];
  action: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ============================================================================
// Recent Commands Storage
// ============================================================================

const RECENT_COMMANDS_KEY = "byk-recent-commands";
const MAX_RECENT_COMMANDS = 5;

function getRecentCommands(): string[] {
  try {
    const stored = localStorage.getItem(RECENT_COMMANDS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function addRecentCommand(commandId: string): void {
  try {
    const recent = getRecentCommands().filter((id) => id !== commandId);
    recent.unshift(commandId);
    localStorage.setItem(
      RECENT_COMMANDS_KEY,
      JSON.stringify(recent.slice(0, MAX_RECENT_COMMANDS))
    );
  } catch {
    // Ignore storage errors
  }
}

// ============================================================================
// Category Labels
// ============================================================================

const CATEGORY_LABELS: Record<CommandItem["category"], string> = {
  layout: "Layout Presets",
  view: "View",
  tools: "Tools",
  ai: "AI Features",
  settings: "Settings",
};

const CATEGORY_ORDER: CommandItem["category"][] = [
  "layout",
  "view",
  "tools",
  "ai",
  "settings",
];

// ============================================================================
// Command Palette Component
// ============================================================================

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const [search, setSearch] = useState("");

  // Derive recent commands from localStorage when dialog opens (not state+effect)
  const recentIds = useMemo(() => {
    if (!open) return [];
    return getRecentCommands();
  }, [open]);

  // Store actions
  const {
    applyPreset,
    toggleLeftSidebar,
    toggleRightSidebar,
    setActiveLeftSection,
    setActiveRightSection,
    resetToDefault,
  } = useUIStore();

  const { setViewMode, undo, redo, select } = useFloorPlanStore();

  // Execute command and track in recents
  const executeCommand = useCallback(
    (command: CommandItem) => {
      command.action();
      addRecentCommand(command.id);
      onOpenChange(false);
    },
    [onOpenChange]
  );

  // Define all available commands
  const commands: CommandItem[] = [
    // Layout Presets
    {
      id: "preset-full-canvas",
      label: "Full Canvas",
      description: "Hide all sidebars for maximum canvas space",
      category: "layout",
      shortcut: ["meta", "shift", "1"],
      action: () => applyPreset("full-canvas"),
    },
    {
      id: "preset-design-mode",
      label: "Design Mode",
      description: "Show left sidebar for product selection",
      category: "layout",
      shortcut: ["meta", "shift", "2"],
      action: () => applyPreset("design-mode"),
    },
    {
      id: "preset-properties-mode",
      label: "Properties Mode",
      description: "Show right sidebar for property editing",
      category: "layout",
      shortcut: ["meta", "shift", "3"],
      action: () => applyPreset("properties-mode"),
    },
    {
      id: "preset-split-view",
      label: "Split View",
      description: "Show both sidebars (default)",
      category: "layout",
      shortcut: ["meta", "shift", "4"],
      action: () => applyPreset("split-view"),
    },
    {
      id: "preset-ai-focus",
      label: "AI Focus",
      description: "Expand AI assistant panel",
      category: "layout",
      shortcut: ["meta", "shift", "5"],
      action: () => applyPreset("ai-focus"),
    },
    // View controls
    {
      id: "view-2d",
      label: "2D View",
      description: "Switch to 2D floor plan view",
      category: "view",
      action: () => setViewMode("2d"),
    },
    {
      id: "view-3d",
      label: "3D View",
      description: "Switch to 3D perspective view",
      category: "view",
      action: () => setViewMode("3d"),
    },
    {
      id: "view-split",
      label: "Split View (2D + 3D)",
      description: "Show both 2D and 3D views side by side",
      category: "view",
      action: () => setViewMode("split"),
    },
    // Sidebar toggles
    {
      id: "toggle-left-sidebar",
      label: "Toggle Left Sidebar",
      description: "Show/hide the product catalog sidebar",
      category: "layout",
      shortcut: ["meta", "\\"],
      action: () => toggleLeftSidebar(),
    },
    {
      id: "toggle-right-sidebar",
      label: "Toggle Right Sidebar",
      description: "Show/hide the properties sidebar",
      category: "layout",
      shortcut: ["meta", "shift", "\\"],
      action: () => toggleRightSidebar(),
    },
    // Left sidebar sections
    {
      id: "section-products",
      label: "Products Catalog",
      description: "Browse available products",
      category: "tools",
      action: () => setActiveLeftSection("products"),
    },
    {
      id: "section-layouts",
      label: "Layout Templates",
      description: "Browse layout templates",
      category: "tools",
      action: () => setActiveLeftSection("layouts"),
    },
    {
      id: "section-styles",
      label: "Style Palettes",
      description: "Browse style options",
      category: "tools",
      action: () => setActiveLeftSection("styles"),
    },
    // Right sidebar sections
    {
      id: "section-properties",
      label: "Properties Panel",
      description: "Edit selected item properties",
      category: "tools",
      action: () => setActiveRightSection("properties"),
    },
    {
      id: "section-ai",
      label: "AI Assistant",
      description: "Open AI design assistant",
      category: "ai",
      action: () => setActiveRightSection("ai"),
    },
    {
      id: "section-render",
      label: "Render Settings",
      description: "Configure render options",
      category: "tools",
      action: () => setActiveRightSection("render"),
    },
    {
      id: "section-issues",
      label: "Design Issues",
      description: "View design conflicts and issues",
      category: "tools",
      action: () => setActiveRightSection("issues"),
    },
    {
      id: "section-quote",
      label: "Quote Builder",
      description: "Generate project quote",
      category: "tools",
      action: () => setActiveRightSection("quote"),
    },
    // Edit actions
    {
      id: "undo",
      label: "Undo",
      description: "Undo last action",
      category: "tools",
      shortcut: ["meta", "z"],
      action: () => undo(),
    },
    {
      id: "redo",
      label: "Redo",
      description: "Redo last undone action",
      category: "tools",
      shortcut: ["meta", "shift", "z"],
      action: () => redo(),
    },
    {
      id: "clear-selection",
      label: "Clear Selection",
      description: "Deselect all items",
      category: "tools",
      shortcut: ["Escape"],
      action: () => select(null),
    },
    // Settings
    {
      id: "reset-layout",
      label: "Reset Layout",
      description: "Reset to default panel layout",
      category: "settings",
      action: () => resetToDefault(),
    },
  ];

  // Get recent commands
  const recentCommands = commands.filter((cmd) => recentIds.includes(cmd.id));

  // Group commands by category
  const commandsByCategory = CATEGORY_ORDER.reduce(
    (acc, category) => {
      acc[category] = commands.filter((cmd) => cmd.category === category);
      return acc;
    },
    {} as Record<CommandItem["category"], CommandItem[]>
  );

  // Format shortcut for display
  const formatShortcut = (keys: string[]): string => {
    const isMac =
      typeof navigator !== "undefined" &&
      navigator.platform.toLowerCase().includes("mac");
    return keys
      .map((key) => {
        switch (key) {
          case "meta":
            return isMac ? "\u2318" : "Ctrl";
          case "shift":
            return isMac ? "\u21E7" : "Shift";
          case "alt":
            return isMac ? "\u2325" : "Alt";
          case "\\":
            return "\\";
          default:
            return key.toUpperCase();
        }
      })
      .join(isMac ? "" : "+");
  };

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Command Palette"
      className="fixed inset-0 z-50"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
      />

      {/* Dialog */}
      <div className="fixed left-1/2 top-1/4 w-full max-w-lg -translate-x-1/2 rounded-xl border border-gray-200 bg-white shadow-2xl">
        {/* Search Input */}
        <Command.Input
          value={search}
          onValueChange={setSearch}
          placeholder="Type a command or search..."
          className="w-full border-b border-gray-200 px-4 py-3 text-base outline-none placeholder:text-gray-400"
        />

        {/* Command List */}
        <Command.List className="max-h-80 overflow-y-auto p-2">
          <Command.Empty className="py-6 text-center text-sm text-gray-500">
            No results found.
          </Command.Empty>

          {/* Recent Commands */}
          {recentCommands.length > 0 && !search && (
            <Command.Group heading="Recent">
              {recentCommands.map((command) => (
                <Command.Item
                  key={`recent-${command.id}`}
                  value={command.label}
                  onSelect={() => executeCommand(command)}
                  className="flex cursor-pointer items-center justify-between rounded-lg px-3 py-2 text-sm hover:bg-gray-100 aria-selected:bg-gray-100"
                >
                  <div>
                    <div className="font-medium">{command.label}</div>
                    {command.description && (
                      <div className="text-xs text-gray-500">
                        {command.description}
                      </div>
                    )}
                  </div>
                  {command.shortcut && (
                    <kbd className="ml-2 rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-500">
                      {formatShortcut(command.shortcut)}
                    </kbd>
                  )}
                </Command.Item>
              ))}
            </Command.Group>
          )}

          {/* Categorized Commands */}
          {CATEGORY_ORDER.map((category) => {
            const categoryCommands = commandsByCategory[category];
            if (categoryCommands.length === 0) return null;

            return (
              <Command.Group key={category} heading={CATEGORY_LABELS[category]}>
                {categoryCommands.map((command) => (
                  <Command.Item
                    key={command.id}
                    value={`${command.label} ${command.description || ""}`}
                    onSelect={() => executeCommand(command)}
                    className="flex cursor-pointer items-center justify-between rounded-lg px-3 py-2 text-sm hover:bg-gray-100 aria-selected:bg-gray-100"
                  >
                    <div>
                      <div className="font-medium">{command.label}</div>
                      {command.description && (
                        <div className="text-xs text-gray-500">
                          {command.description}
                        </div>
                      )}
                    </div>
                    {command.shortcut && (
                      <kbd className="ml-2 rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-500">
                        {formatShortcut(command.shortcut)}
                      </kbd>
                    )}
                  </Command.Item>
                ))}
              </Command.Group>
            );
          })}
        </Command.List>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-2 text-xs text-gray-500">
          <div className="flex items-center gap-4">
            <span>
              <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono">
                {"\u2191\u2193"}
              </kbd>{" "}
              Navigate
            </span>
            <span>
              <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono">
                {"\u21B5"}
              </kbd>{" "}
              Select
            </span>
            <span>
              <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono">
                Esc
              </kbd>{" "}
              Close
            </span>
          </div>
        </div>
      </div>
    </Command.Dialog>
  );
}

export default CommandPalette;
